import React from 'react';

import { createEarningsColumns } from './columns/earningsColumns';
import {
  BaseCalendar,
  EarningsCard,
  BaseCalendarPageProps,
  EarningsItem,
  applyDateFilter,
  applyWatchlistFilter,
  applyGenericFilter,
} from 'screens/Calendar';

const calculatePriceTargetTrend = (pt: any) => {
  if (!pt || !pt.lqapt || pt.lqapt === 0) {
    return null;
  }

  const lastMonthAvgPT = pt.lmapt || 0;
  const lastQuarterAvgPT = pt.lqapt;

  if (pt.lmc === 0) {
    return null;
  }

  return (lastMonthAvgPT - lastQuarterAvgPT) / lastQuarterAvgPT;
};

const EarningsCalendarPage: React.FC<BaseCalendarPageProps> = (props) => {
  const columnWidths = [8, 8, 10, 9, 8, 8, 8, 12, 10];
  const transformData = (data: any[]) => {
    const transformed = data.flatMap((day) => {
      return day.items.map((item: EarningsItem) => ({
        symbol: item.t,
        mc: item.mc,
        marketCap: item.mc,
        date: item.d,
        epsActual: item.ea,
        epsEstimated: item.ee,
        revenueActual: item.ra,
        revenueEstimated: item.re,
        priceTargetTrend: item?.pt ? calculatePriceTargetTrend(item.pt) : '--',
        consensus: item.ar?.c ?? '--',
        time: item.ty || '-',
      }));
    });

    return transformed;
  };

  const filterData = (transformedData: any[], filterOptions: any) => {
    const isTickerSpecific =
      filterOptions.selectedDate === null &&
      filterOptions.fromDate === null &&
      filterOptions.toDate === null &&
      filterOptions.selectedTimeframe === null;

    if (isTickerSpecific) {
      return transformedData;
    }

    let filteredItems = applyDateFilter(transformedData, filterOptions);

    if (!filterOptions.selectedThirdSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedThirdSectionFilters,
        'time',
        (item, filter) => item.time && item.time.includes(filter),
      );
    }

    if (!filterOptions.selectedFourthSectionFilters.has('all')) {
      filteredItems = applyGenericFilter(
        filteredItems,
        filterOptions.selectedFourthSectionFilters,
        'consensus',
        (item, filter) => {
          const consensus = item.consensus;
          if (!consensus || consensus === '--') {
            return filter === 'none' || filter === '--';
          }
          return consensus === filter;
        },
      );
    }

    const finalFiltered = applyWatchlistFilter(
      filteredItems,
      filterOptions.isWatchListOnly,
      filterOptions.bookmarkedItems,
    );

    return finalFiltered;
  };

  return (
    <BaseCalendar
      {...props}
      symbol={props?.symbol ?? ''}
      screenType="earnings"
      title="Earnings Calendar"
      columns={createEarningsColumns}
      columnWidths={columnWidths}
      mobileCard={EarningsCard}
      filterData={filterData}
      transformData={transformData}
      slug={props.slug}
    />
  );
};

export default EarningsCalendarPage;
